(function () {
  "use strict";

  const MIN_FREQ = 20;
  const MAX_FREQ = 20000;
  const STORAGE_KEY = "biopods-resonance-generator-v1";
  const root = document.querySelector("[data-biopods-resonance-generator]");

  if (!root || root.dataset.generatorReady === "true") {
    return;
  }

  root.dataset.generatorReady = "true";
  const qs = root.querySelector.bind(root);
  const qsa = root.querySelectorAll.bind(root);
  const INSTRUCTIONS_STORAGE_KEY = "biopods-resonance-instructions-seen";
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;

  function detectLocale() {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("lang") || root.dataset.locale || document.documentElement.lang || navigator.language || "en";
    return /^fr\b/i.test(requested) ? "fr" : "en";
  }

  const translations = {
    en: {
      defaults: {
        intention: "Transformation"
      },
      static: {
        eyebrow: "Solfeggio + Binaural + Resonance",
        title: "Biopods Resonance Generator",
        chooseHeading: "Choose Your Session",
        chooseNote: "Select Insole Tuning, Therapeutic Use, or Meditation first. Then choose Phone or Computer. Then open Use Instructions. The app will hide controls that do not apply to that setup.",
        useLegend: "Use",
        playbackLegend: "Playback",
        selectedSetup: "Selected Setup",
        useInstructions: "Use Instructions",
        insoleTuning: "Insole Tuning",
        insoleTuningNote: "Solfeggio tone for the crystal matrix.",
        therapeuticUse: "Therapeutic Use",
        therapeuticUseNote: "Sound session with optional headphones.",
        meditation: "Meditation",
        meditationNote: "Relaxed listening with optional beats.",
        tuneWithPhone: "Tune with Phone",
        tuneWithPhoneNote: "Place insoles 4 in / 10 cm away.",
        tuneWithComputer: "Tune with Computer",
        tuneWithComputerNote: "Place insoles close to the speakers.",
        timer: "Timer",
        masterVolume: "Master Volume",
        customFrequency: "Custom Frequency",
        frequencySteps: "Frequency Steps",
        downTen: "Down 10 Hz",
        downOne: "Down 1 Hz",
        upOne: "Up 1 Hz",
        upTen: "Up 10 Hz",
        solfeggioHeading: "Solfeggio Frequencies",
        solfeggioNote: "Start here. Choose the tone that best matches the session intention.",
        reset: "Reset",
        resetTitle: "Reset to 528 Hz",
        frequencyGuide: "What Frequency Should I Choose?",
        binauralHeading: "Binaural Beats",
        binauralNote: "Stereo headphones required.",
        beatOffset: "Beat Offset",
        modePreset: "Mode Preset",
        readyToPlay: "Ready to play",
        instructionsEyebrow: "Setup guide",
        closeInstructions: "Close instructions",
        gotIt: "Got it"
      },
      ui: {
        ready: "Ready",
        resonating: "Resonating",
        unsupported: "Unsupported",
        tapAgain: "Tap again",
        startSession: "Start Session",
        stopSession: "Stop Session",
        startResonance: "Start resonance",
        stopResonance: "Stop resonance",
        on: "On",
        off: "Off",
        left: "Left",
        right: "Right",
        phone: "phone",
        computer: "computer",
        customFrequency: "Custom Frequency",
        hideCustomFrequency: "Hide Custom Frequency",
        customResonance: "Custom Resonance",
        closestSolfeggio: "Closest Solfeggio",
        modeSummary(summary, device) {
          return `${summary} by ${device}`;
        }
      },
      solfeggioPresets: [
        { frequency: 174, label: "174 Hz", meta: "Comfort", intention: "Comfort", guide: "For grounding, body ease, and a softer starting point." },
        { frequency: 285, label: "285 Hz", meta: "Restore", intention: "Restoration", guide: "For renewal, tissue-focused sessions, and recovery rituals." },
        { frequency: 396, label: "396 Hz", meta: "Release", intention: "Release", guide: "For grounding, letting go, and clearing stuck tension." },
        { frequency: 417, label: "417 Hz", meta: "Reset", intention: "Reset", guide: "For transition, reset, and moving into a new state." },
        { frequency: 528, label: "528 Hz", meta: "Transform", intention: "Transformation", guide: "For transformation, harmony, and the classic Solfeggio center point." },
        { frequency: 639, label: "639 Hz", meta: "Balance", intention: "Balance", guide: "For connection, balance, and relational calm." },
        { frequency: 741, label: "741 Hz", meta: "Clarity", intention: "Clarity", guide: "For clarity, clearing, and focused intention." },
        { frequency: 852, label: "852 Hz", meta: "Align", intention: "Alignment", guide: "For alignment, inner listening, and meditative attention." },
        { frequency: 963, label: "963 Hz", meta: "Expand", intention: "Expansion", guide: "For expansion, stillness, and higher-awareness rituals." }
      ],
      brainwaveModes: [
        { id: "delta", label: "Delta", beat: 2, meta: "Deep rest" },
        { id: "theta", label: "Theta", beat: 6, meta: "Meditation" },
        { id: "alpha", label: "Alpha", beat: 10, meta: "Calm focus" },
        { id: "gamma", label: "Gamma", beat: 40, meta: "Clarity" }
      ],
      binauralProfiles: {
        therapy: {
          description: "Binaural Beats use two slightly different tones, one in each ear, to create a perceived rhythmic beat.",
          guidance: "They are commonly used for relaxation, stress support, sleep support, and calmer body-based sessions."
        },
        meditation: {
          description: "Binaural Beats use two slightly different tones, one in each ear, to create a perceived rhythmic beat.",
          guidance: "Choose Delta for deep rest, Theta for meditation, Alpha for calm focus, or Gamma for mental clarity."
        }
      },
      sessionModes: {
        tune: {
          summary: "Insole tuning",
          phoneTitle: "Tune with Phone",
          computerTitle: "Tune with Computer",
          phoneNote: "Use the phone speaker, or preferably wireless/external speakers for stronger output. Keep insoles 4 in / 10 cm from the phone; if using connected speakers, place insoles close to the speakers and the Resonance Layer can be used.",
          computerNote: "Use computer or external speakers for stronger acoustic output. Place the insoles close to the speakers and turn on the Resonance Layer when you want the speaker surface to help excite the crystal matrix."
        },
        therapy: {
          summary: "Therapeutic use",
          phoneTitle: "Use with Phone",
          computerTitle: "Use with Computer",
          phoneNote: "Use the phone speaker or connected wireless/external speakers for the Solfeggio tone. Binaural Beats benefits require stereo headphones; Vibroacoustic Support is for speaker sessions.",
          computerNote: "Use computer or external speakers for the Solfeggio tone. Binaural Beats benefits are only achieved with stereo headphones. Vibroacoustic Support is available for speaker sessions."
        },
        meditation: {
          summary: "Meditation",
          phoneTitle: "Use with Phone",
          computerTitle: "Use with Computer",
          phoneNote: "Use the phone speaker or connected wireless/external speakers for the Solfeggio tone. Binaural Beats benefits require stereo headphones; Grounding Pulse is for speaker sessions.",
          computerNote: "Use computer or external speakers for the Solfeggio tone. Binaural Beats benefits are only achieved with stereo headphones. Grounding Pulse is available as a gentle speaker layer."
        }
      },
      resonanceProfiles: {
        tune: {
          title: "Resonance Layer",
          note: "External speaker sessions only.",
          description: "Helps desktop or external speakers physically excite the insole crystals, while the Solfeggio tone remains the audible tuning frequency.",
          usageNote: "Use this layer only with connected external speakers, not headphones. Place the insoles close to the speaker surface so the low-frequency pulse can transfer through the air and nearby surface.",
          presetName: "Crystal Excitation",
          presetDetail: "48 Hz square pulse, moderate blend",
          pulseHz: 48,
          pulseMix: 18,
          waveform: "square"
        },
        therapy: {
          title: "Vibroacoustic Support",
          note: "Optional speaker-based body layer.",
          description: "Low-frequency acoustic vibration is the basis of vibroacoustic therapy, commonly explored for relaxation, pain support, stress reduction, and body-based calming.",
          usageNote: "Use this layer for speaker sessions. For headphones-only Binaural Beats, leave it off.",
          presetName: "Body Calm",
          presetDetail: "40 Hz smooth triangle wave, gentle blend",
          pulseHz: 40,
          pulseMix: 9,
          waveform: "triangle"
        },
        meditation: {
          title: "Grounding Pulse",
          note: "Gentle speaker layer for meditation.",
          description: "The main meditative brainwave feature is still Binaural Beats while the Grounding Pulse enhances body awareness.",
          usageNote: "Use this layer for speaker-based meditation. For headphones-only Binaural Beats, leave it off.",
          presetName: "Body Anchor",
          presetDetail: "36 Hz smooth sine wave, subtle blend",
          pulseHz: 36,
          pulseMix: 6,
          waveform: "sine"
        }
      },
      instructionProfiles: {
        tune: {
          eyebrow: "Insole Tuning Setup",
          base: [
            { title: "Controls", body: "Select a Solfeggio preset or set a custom frequency, choose a 5 to 60 minute timer, then press play. Keep master volume comfortable." }
          ],
          phone: [
            { title: "Mobile Phone Placement", body: "Lay the phone flat with the speaker unobstructed. Place one insole on each side of the phone, about 4 in / 10 cm away, with no direct contact between the phone and the insole magnets." },
            { title: "Why Keep Space Around Phones?", body: "The insoles contain strong neodymium magnets. Keeping space around phones helps protect compass and motion sensors, magnetic accessories, and wireless charging alignment." }
          ],
          computer: [
            { title: "Computer Placement", body: "For laptop, desktop, or external speaker tuning, place the insoles as close to the speakers as practical. Use the Resonance Layer at low volume first, then increase only if the speaker and surface remain stable." }
          ],
          important: [
            { title: "Important", body: "Keep insoles away from pacemakers and implanted medical devices unless cleared by the device guidance or clinician.", important: true }
          ]
        },
        therapy: {
          eyebrow: "Therapeutic Use Setup",
          base: [
            { title: "Controls", body: "Choose Phone or Computer, select a Solfeggio frequency for the main tone, choose a 5 to 60 minute timer, then press play. Keep master volume comfortable." },
            { title: "Solfeggio Frequencies", body: "Select a Solfeggio preset or open Custom Frequency when you want manual control." },
            { title: "Binaural Beats - Stereo Headphones Required", body: "Binaural Beats use slightly different left and right tones to create a perceived rhythmic beat. Delta, Theta, Alpha, and Gamma modes set the selected beat offset." }
          ],
          phone: [],
          computer: [
            { title: "Vibroacoustic Support", body: "For computer or external speaker sessions, use Vibroacoustic Support at low volume first, then increase only if the speaker and surface remain stable." }
          ]
        },
        meditation: {
          eyebrow: "Meditation Setup",
          base: [
            { title: "Controls", body: "Choose Phone or Computer, select a Solfeggio frequency for the main tone, choose a 5 to 60 minute timer, then press play. Use stereo headphones when turning on Binaural Beats." },
            { title: "Solfeggio Frequencies", body: "Select a Solfeggio preset or open Custom Frequency when you want manual control." },
            { title: "Binaural Beats - Stereo Headphones Required", body: "Choose Delta for deep rest, Theta for meditation, Alpha for calm focus, or Gamma for mental clarity." }
          ],
          phone: [],
          computer: [
            { title: "Grounding Pulse", body: "For computer or external speaker meditation, use Grounding Pulse at low volume as a gentle body-awareness layer. For headphones-only Binaural Beats, leave it off." }
          ]
        }
      }
    },
    fr: {
      defaults: {
        intention: "Transformation"
      },
      static: {
        eyebrow: "Solfeggio + binaural + résonance",
        title: "Générateur de résonance Biopods",
        chooseHeading: "Choisissez votre séance",
        chooseNote: "Sélectionnez d'abord le réglage des semelles, l'utilisation thérapeutique ou la méditation. Choisissez ensuite téléphone ou ordinateur, puis ouvrez les instructions d'utilisation. L'application masque les réglages qui ne s'appliquent pas à votre configuration.",
        useLegend: "Utilisation",
        playbackLegend: "Lecture",
        selectedSetup: "Configuration sélectionnée",
        useInstructions: "Instructions d'utilisation",
        insoleTuning: "Réglage des semelles",
        insoleTuningNote: "Tonalité Solfeggio pour la matrice cristalline.",
        therapeuticUse: "Utilisation thérapeutique",
        therapeuticUseNote: "Séance sonore avec écouteurs en option.",
        meditation: "Méditation",
        meditationNote: "Écoute relaxante avec battements en option.",
        tuneWithPhone: "Régler avec un téléphone",
        tuneWithPhoneNote: "Placez les semelles à 4 po / 10 cm.",
        tuneWithComputer: "Régler avec un ordinateur",
        tuneWithComputerNote: "Placez les semelles près des haut-parleurs.",
        timer: "Minuterie",
        masterVolume: "Volume principal",
        customFrequency: "Fréquence personnalisée",
        frequencySteps: "Pas de fréquence",
        downTen: "Diminuer de 10 Hz",
        downOne: "Diminuer de 1 Hz",
        upOne: "Augmenter de 1 Hz",
        upTen: "Augmenter de 10 Hz",
        solfeggioHeading: "Fréquences Solfeggio",
        solfeggioNote: "Commencez ici. Choisissez la tonalité qui correspond le mieux à l'intention de la séance.",
        reset: "Réinitialiser",
        resetTitle: "Réinitialiser à 528 Hz",
        frequencyGuide: "Quelle fréquence choisir?",
        binauralHeading: "Battements binauraux",
        binauralNote: "Écouteurs stéréo requis.",
        beatOffset: "Écart de battement",
        modePreset: "Préréglage du mode",
        readyToPlay: "Prêt à jouer",
        instructionsEyebrow: "Guide de configuration",
        closeInstructions: "Fermer les instructions",
        gotIt: "Compris"
      },
      ui: {
        ready: "Prêt",
        resonating: "En résonance",
        unsupported: "Non pris en charge",
        tapAgain: "Touchez encore",
        startSession: "Démarrer la séance",
        stopSession: "Arrêter la séance",
        startResonance: "Démarrer la résonance",
        stopResonance: "Arrêter la résonance",
        on: "Marche",
        off: "Arrêt",
        left: "Gauche",
        right: "Droite",
        phone: "avec téléphone",
        computer: "avec ordinateur",
        customFrequency: "Fréquence personnalisée",
        hideCustomFrequency: "Masquer la fréquence personnalisée",
        customResonance: "Résonance personnalisée",
        closestSolfeggio: "Solfeggio le plus proche",
        modeSummary(summary, device) {
          return `${summary} ${device}`;
        }
      },
      solfeggioPresets: [
        { frequency: 174, label: "174 Hz", meta: "Confort", intention: "Confort", guide: "Pour l'ancrage, l'aisance corporelle et un point de départ plus doux." },
        { frequency: 285, label: "285 Hz", meta: "Restaurer", intention: "Restauration", guide: "Pour le renouveau, les séances axées sur les tissus et les rituels de récupération." },
        { frequency: 396, label: "396 Hz", meta: "Libérer", intention: "Libération", guide: "Pour l'ancrage, le relâchement et la libération des tensions stagnantes." },
        { frequency: 417, label: "417 Hz", meta: "Réinitialiser", intention: "Réinitialisation", guide: "Pour la transition, la réinitialisation et le passage vers un nouvel état." },
        { frequency: 528, label: "528 Hz", meta: "Transformer", intention: "Transformation", guide: "Pour la transformation, l'harmonie et le point central classique des Solfeggio." },
        { frequency: 639, label: "639 Hz", meta: "Équilibrer", intention: "Équilibre", guide: "Pour la connexion, l'équilibre et le calme relationnel." },
        { frequency: 741, label: "741 Hz", meta: "Clarté", intention: "Clarté", guide: "Pour la clarté, le dégagement et l'intention ciblée." },
        { frequency: 852, label: "852 Hz", meta: "Aligner", intention: "Alignement", guide: "Pour l'alignement, l'écoute intérieure et l'attention méditative." },
        { frequency: 963, label: "963 Hz", meta: "Expansion", intention: "Expansion", guide: "Pour l'expansion, le calme intérieur et les rituels de conscience élevée." }
      ],
      brainwaveModes: [
        { id: "delta", label: "Delta", beat: 2, meta: "Repos profond" },
        { id: "theta", label: "Thêta", beat: 6, meta: "Méditation" },
        { id: "alpha", label: "Alpha", beat: 10, meta: "Calme et concentration" },
        { id: "gamma", label: "Gamma", beat: 40, meta: "Clarté" }
      ],
      binauralProfiles: {
        therapy: {
          description: "Les battements binauraux utilisent deux tonalités légèrement différentes, une dans chaque oreille, afin de créer un battement rythmique perçu.",
          guidance: "Ils sont couramment utilisés pour la relaxation, le soutien au stress, le soutien au sommeil et les séances apaisantes centrées sur le corps."
        },
        meditation: {
          description: "Les battements binauraux utilisent deux tonalités légèrement différentes, une dans chaque oreille, afin de créer un battement rythmique perçu.",
          guidance: "Choisissez Delta pour le repos profond, Thêta pour la méditation, Alpha pour le calme concentré ou Gamma pour la clarté mentale."
        }
      },
      sessionModes: {
        tune: {
          summary: "Réglage des semelles",
          phoneTitle: "Régler avec un téléphone",
          computerTitle: "Régler avec un ordinateur",
          phoneNote: "Utilisez le haut-parleur du téléphone ou, de préférence, des haut-parleurs sans fil/externes pour une sortie plus puissante. Gardez les semelles à 4 po / 10 cm du téléphone; avec des haut-parleurs connectés, placez-les près des haut-parleurs et activez la couche de résonance au besoin.",
          computerNote: "Utilisez l'ordinateur ou des haut-parleurs externes pour une sortie acoustique plus puissante. Placez les semelles près des haut-parleurs et activez la couche de résonance lorsque vous voulez que la surface du haut-parleur aide à stimuler la matrice cristalline."
        },
        therapy: {
          summary: "Utilisation thérapeutique",
          phoneTitle: "Utiliser avec un téléphone",
          computerTitle: "Utiliser avec un ordinateur",
          phoneNote: "Utilisez le haut-parleur du téléphone ou des haut-parleurs sans fil/externes connectés pour la tonalité Solfeggio. Les bienfaits des battements binauraux nécessitent des écouteurs stéréo; le soutien vibroacoustique est destiné aux séances avec haut-parleurs.",
          computerNote: "Utilisez l'ordinateur ou des haut-parleurs externes pour la tonalité Solfeggio. Les bienfaits des battements binauraux sont obtenus seulement avec des écouteurs stéréo. Le soutien vibroacoustique est disponible pour les séances avec haut-parleurs."
        },
        meditation: {
          summary: "Méditation",
          phoneTitle: "Utiliser avec un téléphone",
          computerTitle: "Utiliser avec un ordinateur",
          phoneNote: "Utilisez le haut-parleur du téléphone ou des haut-parleurs sans fil/externes connectés pour la tonalité Solfeggio. Les bienfaits des battements binauraux nécessitent des écouteurs stéréo; l'impulsion d'ancrage est destinée aux séances avec haut-parleurs.",
          computerNote: "Utilisez l'ordinateur ou des haut-parleurs externes pour la tonalité Solfeggio. Les bienfaits des battements binauraux sont obtenus seulement avec des écouteurs stéréo. L'impulsion d'ancrage est disponible comme couche douce avec haut-parleurs."
        }
      },
      resonanceProfiles: {
        tune: {
          title: "Couche de résonance",
          note: "Séances avec haut-parleurs externes seulement.",
          description: "Aide les haut-parleurs de bureau ou externes à stimuler physiquement les cristaux des semelles, tandis que la tonalité Solfeggio demeure la fréquence de réglage audible.",
          usageNote: "Utilisez cette couche seulement avec des haut-parleurs externes connectés, pas avec des écouteurs. Placez les semelles près de la surface du haut-parleur afin que l'impulsion basse fréquence puisse se transmettre dans l'air et par la surface voisine.",
          presetName: "Stimulation cristalline",
          presetDetail: "Impulsion carrée de 48 Hz, mélange modéré",
          pulseHz: 48,
          pulseMix: 18,
          waveform: "square"
        },
        therapy: {
          title: "Soutien vibroacoustique",
          note: "Couche corporelle facultative avec haut-parleurs.",
          description: "Les vibrations acoustiques à basse fréquence sont à la base de la thérapie vibroacoustique, couramment explorée pour la relaxation, le soutien de la douleur, la réduction du stress et l'apaisement corporel.",
          usageNote: "Utilisez cette couche pour les séances avec haut-parleurs. Pour les battements binauraux avec écouteurs seulement, laissez-la désactivée.",
          presetName: "Calme corporel",
          presetDetail: "Onde triangulaire douce de 40 Hz, mélange léger",
          pulseHz: 40,
          pulseMix: 9,
          waveform: "triangle"
        },
        meditation: {
          title: "Impulsion d'ancrage",
          note: "Couche douce avec haut-parleurs pour la méditation.",
          description: "La fonction méditative principale demeure les battements binauraux, tandis que l'impulsion d'ancrage renforce la conscience corporelle.",
          usageNote: "Utilisez cette couche pour la méditation avec haut-parleurs. Pour les battements binauraux avec écouteurs seulement, laissez-la désactivée.",
          presetName: "Ancrage corporel",
          presetDetail: "Onde sinusoïdale douce de 36 Hz, mélange subtil",
          pulseHz: 36,
          pulseMix: 6,
          waveform: "sine"
        }
      },
      instructionProfiles: {
        tune: {
          eyebrow: "Configuration du réglage des semelles",
          base: [
            { title: "Contrôles", body: "Sélectionnez un préréglage Solfeggio ou réglez une fréquence personnalisée, choisissez une minuterie de 5 à 60 minutes, puis appuyez sur lecture. Gardez le volume principal confortable." }
          ],
          phone: [
            { title: "Placement avec téléphone mobile", body: "Déposez le téléphone à plat avec le haut-parleur dégagé. Placez une semelle de chaque côté du téléphone, à environ 4 po / 10 cm, sans contact direct entre le téléphone et les aimants des semelles." },
            { title: "Pourquoi garder un espace autour des téléphones?", body: "Les semelles contiennent de puissants aimants au néodyme. Garder un espace autour des téléphones aide à protéger la boussole et les capteurs de mouvement, les accessoires magnétiques et l'alignement de la recharge sans fil." }
          ],
          computer: [
            { title: "Placement avec ordinateur", body: "Pour le réglage avec ordinateur portable, ordinateur de bureau ou haut-parleurs externes, placez les semelles aussi près que possible des haut-parleurs. Utilisez d'abord la couche de résonance à faible volume, puis augmentez seulement si le haut-parleur et la surface demeurent stables." }
          ],
          important: [
            { title: "Important", body: "Gardez les semelles éloignées des stimulateurs cardiaques et des dispositifs médicaux implantés, sauf indication contraire du fabricant du dispositif ou d'un clinicien.", important: true }
          ]
        },
        therapy: {
          eyebrow: "Configuration de l'utilisation thérapeutique",
          base: [
            { title: "Contrôles", body: "Choisissez téléphone ou ordinateur, sélectionnez une fréquence Solfeggio comme tonalité principale, choisissez une minuterie de 5 à 60 minutes, puis appuyez sur lecture. Gardez le volume principal confortable." },
            { title: "Fréquences Solfeggio", body: "Sélectionnez un préréglage Solfeggio ou ouvrez la fréquence personnalisée lorsque vous voulez un contrôle manuel." },
            { title: "Battements binauraux - écouteurs stéréo requis", body: "Les battements binauraux utilisent des tonalités gauche et droite légèrement différentes pour créer un battement rythmique perçu. Les modes Delta, Thêta, Alpha et Gamma règlent l'écart de battement sélectionné." }
          ],
          phone: [],
          computer: [
            { title: "Soutien vibroacoustique", body: "Pour les séances avec ordinateur ou haut-parleurs externes, utilisez le soutien vibroacoustique à faible volume d'abord, puis augmentez seulement si le haut-parleur et la surface demeurent stables." }
          ]
        },
        meditation: {
          eyebrow: "Configuration de la méditation",
          base: [
            { title: "Contrôles", body: "Choisissez téléphone ou ordinateur, sélectionnez une fréquence Solfeggio comme tonalité principale, choisissez une minuterie de 5 à 60 minutes, puis appuyez sur lecture. Utilisez des écouteurs stéréo lorsque vous activez les battements binauraux." },
            { title: "Fréquences Solfeggio", body: "Sélectionnez un préréglage Solfeggio ou ouvrez la fréquence personnalisée lorsque vous voulez un contrôle manuel." },
            { title: "Battements binauraux - écouteurs stéréo requis", body: "Choisissez Delta pour le repos profond, Thêta pour la méditation, Alpha pour le calme concentré ou Gamma pour la clarté mentale." }
          ],
          phone: [],
          computer: [
            { title: "Impulsion d'ancrage", body: "Pour la méditation avec ordinateur ou haut-parleurs externes, utilisez l'impulsion d'ancrage à faible volume comme couche douce de conscience corporelle. Pour les battements binauraux avec écouteurs seulement, laissez-la désactivée." }
          ]
        }
      }
    }
  };

  const locale = detectLocale();
  const copy = translations[locale] || translations.en;
  const solfeggioPresets = copy.solfeggioPresets;
  const brainwaveModes = copy.brainwaveModes;
  const binauralProfiles = copy.binauralProfiles;
  const sessionModes = copy.sessionModes;
  const resonanceProfiles = copy.resonanceProfiles;
  const instructionProfiles = copy.instructionProfiles;

  const state = {
    frequency: 528,
    selectedPreset: 528,
    intention: copy.defaults.intention,
    volume: 14,
    playing: false,
    timerMinutes: 15,
    timerStartedAt: 0,
    timerEndsAt: 0,
    timerComplete: false,
    useMode: "tune",
    deviceMode: "phone",
    customOpen: false,
    binauralEnabled: false,
    beatMode: "theta",
    beatHz: 6,
    resonanceEnabled: false,
    pulseHz: 48,
    pulseMix: 18,
    pulseWaveform: "square",
    visualPhase: 0
  };

  const audio = {
    context: null,
    masterGain: null,
    carrierOsc: null,
    carrierGain: null,
    leftOsc: null,
    rightOsc: null,
    leftGain: null,
    rightGain: null,
    merger: null,
    pulseOsc: null,
    pulseGain: null,
    nodes: []
  };

  const screenWakeLock = {
    sentinel: null,
    pending: null
  };

  const el = {
    canvas: qs("#mandalaCanvas"),
    frequencyReadout: qs("#frequencyReadout"),
    frequencyInput: qs("#frequencyInput"),
    frequencySlider: qs("#frequencySlider"),
    modeLabel: qs("#modeLabel"),
    playbackStatus: qs("#playbackStatus"),
    transportButton: qs("#transportButton"),
    transportLabel: qs("#transportLabel"),
    volumeSlider: qs("#volumeSlider"),
    volumeReadout: qs("#volumeReadout"),
    presetGrid: qs("#presetGrid"),
    resetButton: qs("#resetButton"),
    timerReadout: qs("#timerReadout"),
    timerProgress: qs("#timerProgress"),
    binauralToggle: qs("#binauralToggle"),
    binauralDescription: qs("#binauralDescription"),
    binauralGuidance: qs("#binauralGuidance"),
    brainwaveGrid: qs("#brainwaveGrid"),
    beatInput: qs("#beatInput"),
    stereoReadout: qs("#stereoReadout"),
    resonanceToggle: qs("#resonanceToggle"),
    resonanceTitle: qs("#resonanceTitle"),
    resonanceNote: qs("#resonanceNote"),
    resonanceDescription: qs("#resonanceDescription"),
    resonanceUsageNote: qs("#resonanceUsageNote"),
    resonancePresetName: qs("#resonancePresetName"),
    resonancePresetDetail: qs("#resonancePresetDetail"),
    instructionsButton: qs("#instructionsButton"),
    instructionsModal: qs("#instructionsModal"),
    instructionsEyebrow: qs("#instructionsEyebrow"),
    instructionsBody: qs("#instructionsBody"),
    closeInstructions: qs("#closeInstructions"),
    gotItButton: qs("#gotItButton"),
    modeSummary: qs("#modeSummary"),
    setupModeTitle: qs("#setupModeTitle"),
    setupModeNote: qs("#setupModeNote"),
    phoneOptionTitle: qs("#phoneOptionTitle"),
    computerOptionTitle: qs("#computerOptionTitle"),
    customToggle: qs("#customToggle"),
    customFrequencyPanel: qs("#customFrequencyPanel"),
    frequencyBenefit: qs("#frequencyBenefit"),
    frequencyGuideList: qs("#frequencyGuideList"),
    binauralPanel: qs(".binaural-panel"),
    resonancePanel: qs(".resonance-panel")
  };

  const ctx = el.canvas.getContext("2d");

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function parseNumber(value, fallback, min, max) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? clamp(parsed, min, max) : fallback;
  }

  function formatFrequency(value) {
    return value >= 1000 ? value.toFixed(1) : value.toFixed(2);
  }

  function frequencyToSlider(frequency) {
    const min = Math.log10(MIN_FREQ);
    const max = Math.log10(MAX_FREQ);
    const current = Math.log10(clamp(frequency, MIN_FREQ, MAX_FREQ));
    return Math.round(((current - min) / (max - min)) * 1000);
  }

  function sliderToFrequency(value) {
    const min = Math.log10(MIN_FREQ);
    const max = Math.log10(MAX_FREQ);
    const ratio = Number(value) / 1000;
    return 10 ** (min + ratio * (max - min));
  }

  function formatTimer(milliseconds) {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  function nearestSolfeggio(frequency) {
    return solfeggioPresets.reduce(function (closest, preset) {
      return Math.abs(preset.frequency - frequency) < Math.abs(closest.frequency - frequency) ? preset : closest;
    }, solfeggioPresets[0]);
  }

  function frequencyBenefitText(frequency) {
    const nearest = nearestSolfeggio(frequency);
    const prefix = Math.abs(nearest.frequency - frequency) < 0.01 ? "" : `${copy.ui.closestSolfeggio}: `;
    return `${prefix}${nearest.frequency} Hz - ${nearest.meta}`;
  }

  function setText(selector, text) {
    const node = qs(selector);
    if (node) {
      node.textContent = text;
    }
  }

  function setAttribute(selector, attribute, value) {
    const node = qs(selector);
    if (node) {
      node.setAttribute(attribute, value);
    }
  }

  function translateStaticText() {
    const text = copy.static;
    root.setAttribute("lang", locale === "fr" ? "fr-CA" : "en");

    setText(".topbar .eyebrow", text.eyebrow);
    setText(".topbar h1", text.title);
    setText(".setup-panel .panel-heading h2", text.chooseHeading);
    setText(".setup-panel .panel-heading .panel-note", text.chooseNote);
    setText(".purpose-options legend", text.useLegend);
    setText(".purpose-options label:nth-of-type(1) b", text.insoleTuning);
    setText(".purpose-options label:nth-of-type(1) small", text.insoleTuningNote);
    setText(".purpose-options label:nth-of-type(2) b", text.therapeuticUse);
    setText(".purpose-options label:nth-of-type(2) small", text.therapeuticUseNote);
    setText(".purpose-options label:nth-of-type(3) b", text.meditation);
    setText(".purpose-options label:nth-of-type(3) small", text.meditationNote);
    setText(".device-options legend", text.playbackLegend);
    setText(".device-options label:nth-of-type(1) small", text.tuneWithPhoneNote);
    setText(".device-options label:nth-of-type(2) small", text.tuneWithComputerNote);
    setText(".mode-summary-card span", text.selectedSetup);
    setText("#instructionsButton", text.useInstructions);
    setText(".timer-card small", text.timer);
    setText(".timer-options legend", text.timer);
    setText(".volume-grid label", text.masterVolume);
    setText("#customToggle", text.customFrequency);
    setText(".custom-frequency-heading label", text.customFrequency);
    setAttribute("#frequencySlider", "aria-label", text.customFrequency);
    setAttribute(".step-row", "aria-label", text.frequencySteps);
    setAttribute(".step-row button[data-step='-10']", "title", text.downTen);
    setAttribute(".step-row button[data-step='-1']", "title", text.downOne);
    setAttribute(".step-row button[data-step='1']", "title", text.upOne);
    setAttribute(".step-row button[data-step='10']", "title", text.upTen);
    setText(".presets-panel h2", text.solfeggioHeading);
    setText(".presets-panel .panel-note", text.solfeggioNote);
    setText("#resetButton", text.reset);
    setAttribute("#resetButton", "title", text.resetTitle);
    setText(".frequency-guide summary", text.frequencyGuide);
    setText(".binaural-panel h2", text.binauralHeading);
    setText(".binaural-panel .panel-note", text.binauralNote);
    setText(".compact-number label", text.beatOffset);
    setText(".resonance-preset span", text.modePreset);
    setText(".start-panel .action-eyebrow", text.readyToPlay);
    setText("#instructionsTitle", text.useInstructions);
    setAttribute("#closeInstructions", "aria-label", text.closeInstructions);
    setAttribute("#closeInstructions", "title", text.closeInstructions);
    setText("#gotItButton", text.gotIt);
  }

  function currentSessionMode() {
    return sessionModes[state.useMode] || sessionModes.tune;
  }

  function currentBinauralProfile() {
    return binauralProfiles[state.useMode] || binauralProfiles.therapy;
  }

  function updateBinauralPanel() {
    const profile = currentBinauralProfile();
    el.binauralDescription.textContent = profile.description;
    el.binauralGuidance.textContent = profile.guidance;
  }

  function currentInstructionProfile() {
    return instructionProfiles[state.useMode] || instructionProfiles.tune;
  }

  function currentInstructionGroups() {
    const profile = currentInstructionProfile();
    const deviceGroups = state.deviceMode === "computer" ? profile.computer : profile.phone;
    return profile.base.concat(deviceGroups || [], profile.important || []);
  }

  function updateInstructionsContent() {
    const profile = currentInstructionProfile();
    const fragment = document.createDocumentFragment();

    el.instructionsEyebrow.textContent = profile.eyebrow;
    currentInstructionGroups().forEach(function (group) {
      const section = document.createElement("section");
      const heading = document.createElement("h3");
      const paragraph = document.createElement("p");

      section.className = group.important ? "instruction-group is-important" : "instruction-group";
      heading.textContent = group.title;
      paragraph.textContent = group.body;
      section.append(heading, paragraph);
      fragment.appendChild(section);
    });

    el.instructionsBody.replaceChildren(fragment);
  }

  function currentResonanceProfile() {
    return resonanceProfiles[state.useMode] || resonanceProfiles.tune;
  }

  function syncResonanceProfile() {
    const profile = currentResonanceProfile();
    state.pulseHz = profile.pulseHz;
    state.pulseMix = profile.pulseMix;
    state.pulseWaveform = profile.waveform;
    applyNodeValues();
  }

  function updateResonancePanel() {
    const profile = currentResonanceProfile();
    el.resonanceTitle.textContent = profile.title;
    el.resonanceNote.textContent = profile.note;
    el.resonanceDescription.textContent = profile.description;
    el.resonanceUsageNote.textContent = profile.usageNote;
    el.resonancePresetName.textContent = profile.presetName;
    el.resonancePresetDetail.textContent = profile.presetDetail;
  }

  function readStoredState() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (!stored || typeof stored !== "object") {
        return;
      }

      state.frequency = parseNumber(stored.frequency, state.frequency, MIN_FREQ, MAX_FREQ);
      if (Object.prototype.hasOwnProperty.call(stored, "selectedPreset")) {
        state.selectedPreset = Number.isFinite(stored.selectedPreset) ? stored.selectedPreset : null;
      }
      state.intention = typeof stored.intention === "string" ? stored.intention : state.intention;
      state.volume = parseNumber(stored.volume, state.volume, 0, 100);
      state.timerMinutes = parseNumber(stored.timerMinutes, state.timerMinutes, 5, 60);
      state.useMode = sessionModes[stored.useMode] ? stored.useMode : state.useMode;
      state.deviceMode = stored.deviceMode === "computer" ? "computer" : state.deviceMode;
      state.customOpen = Boolean(stored.customOpen);
      state.binauralEnabled = Boolean(stored.binauralEnabled);
      state.beatMode = typeof stored.beatMode === "string" ? stored.beatMode : state.beatMode;
      state.beatHz = parseNumber(stored.beatHz, state.beatHz, 1, 60);
      state.resonanceEnabled = Boolean(stored.resonanceEnabled);
      const localizedPreset = solfeggioPresets.find((preset) => Number(preset.frequency) === Number(state.selectedPreset));
      state.intention = localizedPreset ? localizedPreset.intention : copy.ui.customResonance;
      syncResonanceProfile();
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  function storeState() {
    const serializable = {
      frequency: state.frequency,
      selectedPreset: state.selectedPreset,
      intention: state.intention,
      volume: state.volume,
      timerMinutes: state.timerMinutes,
      useMode: state.useMode,
      deviceMode: state.deviceMode,
      customOpen: state.customOpen,
      binauralEnabled: state.binauralEnabled,
      beatMode: state.beatMode,
      beatHz: state.beatHz,
      resonanceEnabled: state.resonanceEnabled
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  }

  function shouldShowInstructions() {
    try {
      return localStorage.getItem(INSTRUCTIONS_STORAGE_KEY) !== "true";
    } catch (error) {
      return true;
    }
  }

  function markInstructionsSeen() {
    try {
      localStorage.setItem(INSTRUCTIONS_STORAGE_KEY, "true");
    } catch (error) {
      // The modal can still close when storage is unavailable.
    }
  }

  function openInstructions() {
    updateInstructionsContent();
    el.instructionsModal.hidden = false;
    el.instructionsModal.querySelector(".instructions-modal").scrollTop = 0;
    window.setTimeout(function () {
      el.closeInstructions.focus({ preventScroll: true });
    }, 0);
  }

  function closeInstructions() {
    if (el.instructionsModal.hidden) {
      return;
    }

    el.instructionsModal.hidden = true;
    markInstructionsSeen();
    el.instructionsButton.focus({ preventScroll: true });
  }

  function setCustomOpen(open, options) {
    state.customOpen = Boolean(open);
    el.customFrequencyPanel.hidden = !state.customOpen;
    el.customToggle.setAttribute("aria-expanded", String(state.customOpen));
    el.customToggle.textContent = state.customOpen ? copy.ui.hideCustomFrequency : copy.ui.customFrequency;

    if (options?.store !== false) {
      storeState();
    }
  }

  function updateModePresentation() {
    const mode = currentSessionMode();
    const usingComputer = state.deviceMode === "computer";
    const showBinaural = state.useMode !== "tune";
    const showResonance = true;

    syncResonanceProfile();
    root.dataset.useMode = state.useMode;
    root.dataset.deviceMode = state.deviceMode;
    el.phoneOptionTitle.textContent = mode.phoneTitle;
    el.computerOptionTitle.textContent = mode.computerTitle;
    el.modeSummary.textContent = copy.ui.modeSummary(mode.summary, usingComputer ? copy.ui.computer : copy.ui.phone);
    el.setupModeTitle.textContent = usingComputer ? mode.computerTitle : mode.phoneTitle;
    el.setupModeNote.textContent = usingComputer ? mode.computerNote : mode.phoneNote;
    updateBinauralPanel();
    updateResonancePanel();
    updateInstructionsContent();
    el.binauralPanel.hidden = !showBinaural;
    el.resonancePanel.hidden = !showResonance;

    qsa('input[name="useMode"]').forEach(function (input) {
      input.checked = input.value === state.useMode;
    });

    qsa('input[name="deviceMode"]').forEach(function (input) {
      input.checked = input.value === state.deviceMode;
    });

    if (!showBinaural && state.binauralEnabled) {
      updateBinauralEnabled(false);
    }

    if (!showResonance && state.resonanceEnabled) {
      updateResonanceEnabled(false);
    }
  }

  function setUseMode(mode) {
    state.useMode = sessionModes[mode] ? mode : "tune";

    if (state.useMode === "meditation" && state.beatMode !== "theta") {
      state.beatMode = "theta";
      state.beatHz = 6;
      el.beatInput.value = Number(state.beatHz.toFixed(1));
      updateBrainwaveSelection();
      updateStereoReadout();
    }

    updateModePresentation();
    restartAudioGraph();
    storeState();
  }

  function setDeviceMode(mode) {
    state.deviceMode = mode === "computer" ? "computer" : "phone";
    updateModePresentation();
    storeState();
  }

  function ensureAudio() {
    if (!AudioContextClass) {
      el.playbackStatus.textContent = copy.ui.unsupported;
      return null;
    }

    if (!audio.context) {
      audio.context = new AudioContextClass();
      audio.masterGain = audio.context.createGain();
      audio.masterGain.gain.value = 0;
      audio.masterGain.connect(audio.context.destination);
    }

    return audio.context;
  }

  function masterGainValue() {
    return (state.volume / 100) * 0.72;
  }

  function pulseGainValue() {
    return (state.pulseMix / 100) * 0.52;
  }

  function stereoFrequencies() {
    const offset = state.beatHz / 2;
    return {
      left: clamp(state.frequency - offset, MIN_FREQ, MAX_FREQ),
      right: clamp(state.frequency + offset, MIN_FREQ, MAX_FREQ)
    };
  }

  function stopAudioNodes(delay) {
    if (!audio.context) {
      return;
    }

    const stopAt = audio.context.currentTime + (delay || 0.03);
    audio.nodes.forEach(function (node) {
      try {
        node.stop(stopAt);
      } catch (error) {
        // Some nodes may already be stopped during rapid taps.
      }
      node.onended = function () {
        try {
          node.disconnect();
        } catch (error) {
          // Disconnect is best-effort cleanup.
        }
      };
    });

    audio.nodes = [];
    audio.carrierOsc = null;
    audio.carrierGain = null;
    audio.leftOsc = null;
    audio.rightOsc = null;
    audio.leftGain = null;
    audio.rightGain = null;
    audio.merger = null;
    audio.pulseOsc = null;
    audio.pulseGain = null;
  }

  function canUseScreenWakeLock() {
    return "wakeLock" in navigator && typeof navigator.wakeLock?.request === "function";
  }

  async function requestScreenWakeLock() {
    if (!canUseScreenWakeLock() || document.visibilityState !== "visible" || screenWakeLock.sentinel || screenWakeLock.pending) {
      return;
    }

    try {
      screenWakeLock.pending = navigator.wakeLock.request("screen");
      const sentinel = await screenWakeLock.pending;
      screenWakeLock.pending = null;

      if (!state.playing || document.visibilityState !== "visible") {
        try {
          await sentinel.release();
        } catch (error) {
          // Best-effort cleanup if playback stopped while the request was pending.
        }
        return;
      }

      screenWakeLock.sentinel = sentinel;
      sentinel.addEventListener("release", function () {
        if (screenWakeLock.sentinel === sentinel) {
          screenWakeLock.sentinel = null;
        }
      });
    } catch (error) {
      screenWakeLock.pending = null;
      screenWakeLock.sentinel = null;
    }
  }

  async function releaseScreenWakeLock() {
    const sentinel = screenWakeLock.sentinel;
    screenWakeLock.sentinel = null;

    if (!sentinel) {
      return;
    }

    try {
      await sentinel.release();
    } catch (error) {
      // Some browsers release automatically when tabs become hidden.
    }
  }

  function syncScreenWakeLock() {
    if (state.playing && document.visibilityState === "visible") {
      requestScreenWakeLock();
    } else {
      releaseScreenWakeLock();
    }
  }

  function applyMasterVolume() {
    if (!audio.context || !audio.masterGain) {
      return;
    }

    const target = state.playing ? masterGainValue() : 0;
    audio.masterGain.gain.cancelScheduledValues(audio.context.currentTime);
    audio.masterGain.gain.setTargetAtTime(target, audio.context.currentTime, 0.025);
  }

  function applyNodeValues() {
    if (!audio.context) {
      return;
    }

    const now = audio.context.currentTime;
    const stereo = stereoFrequencies();

    if (audio.carrierOsc) {
      audio.carrierOsc.frequency.setTargetAtTime(state.frequency, now, 0.018);
    }

    if (audio.leftOsc && audio.rightOsc) {
      audio.leftOsc.frequency.setTargetAtTime(stereo.left, now, 0.018);
      audio.rightOsc.frequency.setTargetAtTime(stereo.right, now, 0.018);
    }

    if (audio.pulseOsc) {
      audio.pulseOsc.type = state.pulseWaveform;
      audio.pulseOsc.frequency.setTargetAtTime(state.pulseHz, now, 0.025);
    }

    if (audio.pulseGain) {
      audio.pulseGain.gain.setTargetAtTime(state.resonanceEnabled ? pulseGainValue() : 0, now, 0.025);
    }
  }

  function buildAudioGraph() {
    const context = ensureAudio();
    if (!context) {
      return;
    }

    stopAudioNodes(0.01);

    if (state.binauralEnabled) {
      const stereo = stereoFrequencies();
      audio.leftOsc = context.createOscillator();
      audio.rightOsc = context.createOscillator();
      audio.leftGain = context.createGain();
      audio.rightGain = context.createGain();
      audio.merger = context.createChannelMerger(2);

      audio.leftOsc.type = "sine";
      audio.rightOsc.type = "sine";
      audio.leftOsc.frequency.value = stereo.left;
      audio.rightOsc.frequency.value = stereo.right;
      audio.leftGain.gain.value = 0.82;
      audio.rightGain.gain.value = 0.82;

      audio.leftOsc.connect(audio.leftGain);
      audio.rightOsc.connect(audio.rightGain);
      audio.leftGain.connect(audio.merger, 0, 0);
      audio.rightGain.connect(audio.merger, 0, 1);
      audio.merger.connect(audio.masterGain);

      audio.leftOsc.start();
      audio.rightOsc.start();
      audio.nodes.push(audio.leftOsc, audio.rightOsc);
    } else {
      audio.carrierOsc = context.createOscillator();
      audio.carrierGain = context.createGain();
      audio.carrierOsc.type = "sine";
      audio.carrierOsc.frequency.value = state.frequency;
      audio.carrierGain.gain.value = 1;
      audio.carrierOsc.connect(audio.carrierGain);
      audio.carrierGain.connect(audio.masterGain);
      audio.carrierOsc.start();
      audio.nodes.push(audio.carrierOsc);
    }

    if (state.resonanceEnabled) {
      audio.pulseOsc = context.createOscillator();
      audio.pulseGain = context.createGain();
      audio.pulseOsc.type = state.pulseWaveform;
      audio.pulseOsc.frequency.value = state.pulseHz;
      audio.pulseGain.gain.value = pulseGainValue();
      audio.pulseOsc.connect(audio.pulseGain);
      audio.pulseGain.connect(audio.masterGain);
      audio.pulseOsc.start();
      audio.nodes.push(audio.pulseOsc);
    }

    applyNodeValues();
  }

  function startTone(options) {
    const context = ensureAudio();
    if (!context) {
      return;
    }

    try {
      const resume = context.resume();
      if (resume && typeof resume.catch === "function") {
        resume.catch(function () {
          state.playing = false;
          updateTransport();
          el.playbackStatus.textContent = copy.ui.tapAgain;
        });
      }
    } catch (error) {
      el.playbackStatus.textContent = copy.ui.tapAgain;
      return;
    }

    const preserveTimer = Boolean(options?.preserveTimer);
    buildAudioGraph();
    state.playing = true;
    state.timerComplete = false;

    if (!preserveTimer || !state.timerEndsAt) {
      state.timerStartedAt = performance.now();
      state.timerEndsAt = state.timerStartedAt + state.timerMinutes * 60 * 1000;
    }

    applyMasterVolume();
    updateTransport();
    syncScreenWakeLock();
  }

  function stopTone(options) {
    if (audio.context && audio.masterGain) {
      audio.masterGain.gain.cancelScheduledValues(audio.context.currentTime);
      audio.masterGain.gain.setTargetAtTime(0, audio.context.currentTime, 0.018);
      stopAudioNodes(0.08);
    }

    state.playing = false;
    state.timerComplete = Boolean(options?.complete);
    state.timerStartedAt = 0;
    state.timerEndsAt = 0;
    updateTransport();
    updateTimerDisplay(performance.now());
    syncScreenWakeLock();
  }

  function restartAudioGraph() {
    if (state.playing) {
      startTone({ preserveTimer: true });
    } else {
      applyNodeValues();
    }
  }

  function updateTransport() {
    el.transportButton.classList.toggle("is-playing", state.playing);
    el.transportButton.setAttribute("aria-label", state.playing ? copy.ui.stopResonance : copy.ui.startResonance);
    el.transportButton.setAttribute("title", state.playing ? copy.ui.stopResonance : copy.ui.startResonance);
    if (el.transportLabel) {
      el.transportLabel.textContent = state.playing ? copy.ui.stopSession : copy.ui.startSession;
    }
    el.playbackStatus.textContent = state.playing ? copy.ui.resonating : copy.ui.ready;
    el.playbackStatus.classList.toggle("is-playing", state.playing);
  }

  function updateFrequencyControls(options) {
    const syncInputs = options?.syncInputs !== false;
    el.frequencyReadout.textContent = formatFrequency(state.frequency);
    el.modeLabel.textContent = state.intention;
    el.frequencyBenefit.textContent = frequencyBenefitText(state.frequency);

    if (syncInputs) {
      el.frequencyInput.value = Number(state.frequency.toFixed(2));
      el.frequencySlider.value = frequencyToSlider(state.frequency);
    }

    updatePresetSelection();
    updateStereoReadout();
  }

  function setFrequency(value, options) {
    state.frequency = parseNumber(value, state.frequency, MIN_FREQ, MAX_FREQ);

    if (options?.preset) {
      state.selectedPreset = state.frequency;
      state.intention = options.intention;
    } else {
      state.selectedPreset = null;
      state.intention = copy.ui.customResonance;
    }

    updateFrequencyControls();
    applyNodeValues();
    storeState();
  }

  function setTimerMinutes(minutes) {
    state.timerMinutes = parseNumber(minutes, state.timerMinutes, 5, 60);
    state.timerComplete = false;
    qsa('input[name="timer"]').forEach(function (input) {
      input.checked = Number(input.value) === Number(state.timerMinutes);
    });

    if (state.playing) {
      state.timerStartedAt = performance.now();
      state.timerEndsAt = state.timerStartedAt + state.timerMinutes * 60 * 1000;
    }

    updateTimerDisplay(performance.now());
    storeState();
  }

  function updateTimerDisplay(now) {
    const durationMs = state.timerMinutes * 60 * 1000;
    let remaining = durationMs;
    let progress = 0;

    if (state.playing && state.timerEndsAt) {
      remaining = Math.max(0, state.timerEndsAt - now);
      progress = clamp((durationMs - remaining) / durationMs, 0, 1);

      if (remaining <= 0) {
        stopTone({ complete: true });
        return;
      }
    } else if (state.timerComplete) {
      remaining = 0;
      progress = 1;
    }

    el.timerReadout.textContent = formatTimer(remaining);
    el.timerProgress.style.width = `${Math.round(progress * 100)}%`;
  }

  function updateVolume(value) {
    state.volume = parseNumber(value, state.volume, 0, 100);
    el.volumeSlider.value = state.volume;
    el.volumeReadout.textContent = `${Math.round(state.volume)}%`;
    applyMasterVolume();
    storeState();
  }

  function updateBinauralEnabled(enabled) {
    state.binauralEnabled = Boolean(enabled);
    el.binauralToggle.checked = state.binauralEnabled;
    el.binauralToggle.closest(".switch").querySelector("b").textContent = state.binauralEnabled ? copy.ui.on : copy.ui.off;
    updateStereoReadout();
    restartAudioGraph();
    storeState();
  }

  function setBrainwaveMode(modeId) {
    const mode = brainwaveModes.find((item) => item.id === modeId) || brainwaveModes[1];
    state.beatMode = mode.id;
    state.beatHz = mode.beat;
    el.beatInput.value = Number(state.beatHz.toFixed(1));
    updateBrainwaveSelection();
    updateBinauralEnabled(true);
    applyNodeValues();
    storeState();
  }

  function updateBeat(value) {
    state.beatHz = parseNumber(value, state.beatHz, 1, 60);
    state.beatMode = "custom";
    el.beatInput.value = Number(state.beatHz.toFixed(1));
    updateBrainwaveSelection();
    updateStereoReadout();
    applyNodeValues();
    storeState();
  }

  function updateStereoReadout() {
    const stereo = stereoFrequencies();
    el.stereoReadout.textContent = `${copy.ui.left} ${formatFrequency(stereo.left)} Hz / ${copy.ui.right} ${formatFrequency(stereo.right)} Hz`;
  }

  function updateResonanceEnabled(enabled) {
    syncResonanceProfile();
    state.resonanceEnabled = Boolean(enabled);
    el.resonanceToggle.checked = state.resonanceEnabled;
    el.resonanceToggle.closest(".switch").querySelector("b").textContent = state.resonanceEnabled ? copy.ui.on : copy.ui.off;
    restartAudioGraph();
    storeState();
  }

  function createPresets() {
    const fragment = document.createDocumentFragment();

    solfeggioPresets.forEach(function (preset) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.frequency = String(preset.frequency);
      button.dataset.intention = preset.intention;
      button.innerHTML = `<span>${preset.label}</span><small>${preset.meta}</small>`;
      fragment.appendChild(button);
    });

    el.presetGrid.appendChild(fragment);
  }

  function createFrequencyGuide() {
    const fragment = document.createDocumentFragment();

    solfeggioPresets.forEach(function (preset) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.frequency = String(preset.frequency);
      button.dataset.intention = preset.intention;
      button.innerHTML = `<b>${preset.frequency} Hz - ${preset.meta}</b><span>${preset.guide}</span>`;
      fragment.appendChild(button);
    });

    el.frequencyGuideList.appendChild(fragment);
  }

  function createBrainwaveModes() {
    const fragment = document.createDocumentFragment();

    brainwaveModes.forEach(function (mode) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.mode = mode.id;
      button.innerHTML = `<span>${mode.label}</span><small>${mode.beat} Hz ${mode.meta}</small>`;
      fragment.appendChild(button);
    });

    el.brainwaveGrid.appendChild(fragment);
  }

  function updatePresetSelection() {
    el.presetGrid.querySelectorAll("button[data-frequency]").forEach(function (button) {
      button.classList.toggle("is-selected", Number(button.dataset.frequency) === Number(state.selectedPreset));
    });
  }

  function updateBrainwaveSelection() {
    el.brainwaveGrid.querySelectorAll("button[data-mode]").forEach(function (button) {
      button.classList.toggle("is-selected", button.dataset.mode === state.beatMode);
    });
  }

  function sizeCanvas() {
    const rect = el.canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));

    if (el.canvas.width !== width || el.canvas.height !== height) {
      el.canvas.width = width;
      el.canvas.height = height;
    }
  }

  function drawBackground(width, height) {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#111111";
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = "rgba(255, 251, 247, 0.11)";
    ctx.lineWidth = Math.max(1, width / 1100);

    for (let i = 1; i < 8; i += 1) {
      const x = (width / 8) * i;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let i = 1; i < 5; i += 1) {
      const y = (height / 5) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }

  function drawMandala(now) {
    sizeCanvas();

    const width = el.canvas.width;
    const height = el.canvas.height;
    const centerX = width * 0.5;
    const centerY = height * 0.46;
    const scale = Math.min(width, height);
    const beatPulse = state.binauralEnabled ? clamp(state.beatHz / 10, 0.45, 3.2) : 0.65;
    const pulseLayer = state.resonanceEnabled ? clamp(state.pulseHz / 28, 0.75, 3.6) : 0.9;
    const speed = state.playing ? (beatPulse + pulseLayer) * 0.012 : 0.004;
    const expansion = state.playing
      ? 1 + Math.sin(now * 0.0038 + state.visualPhase) * 0.055
      : 1;

    state.visualPhase += speed;
    drawBackground(width, height);

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(state.visualPhase * 0.16);

    const maxRadius = scale * 0.34 * expansion;
    const rings = [0.22, 0.38, 0.55, 0.72, 0.9];

    rings.forEach(function (ring, index) {
      ctx.beginPath();
      ctx.arc(0, 0, maxRadius * ring, 0, Math.PI * 2);
      ctx.strokeStyle = index % 2 === 0 ? "rgba(198, 210, 225, 0.48)" : "rgba(156, 172, 151, 0.44)";
      ctx.lineWidth = Math.max(1.5, scale / (index === 0 ? 180 : 260));
      ctx.stroke();
    });

    const petals = 24;
    for (let i = 0; i < petals; i += 1) {
      const angle = (Math.PI * 2 * i) / petals;
      const inner = maxRadius * (0.18 + (i % 3) * 0.012);
      const outer = maxRadius * (0.86 + Math.sin(state.visualPhase + i) * 0.035);
      const spread = 0.08;

      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
      ctx.lineTo(Math.cos(angle + spread) * outer, Math.sin(angle + spread) * outer);
      ctx.lineTo(Math.cos(angle - spread) * outer, Math.sin(angle - spread) * outer);
      ctx.closePath();
      ctx.fillStyle = i % 2 === 0 ? "rgba(198, 210, 225, 0.14)" : "rgba(235, 229, 214, 0.12)";
      ctx.strokeStyle = i % 2 === 0 ? "rgba(198, 210, 225, 0.56)" : "rgba(156, 172, 151, 0.5)";
      ctx.lineWidth = Math.max(1, scale / 420);
      ctx.fill();
      ctx.stroke();
    }

    for (let i = 0; i < 12; i += 1) {
      const angle = (Math.PI * 2 * i) / 12 - state.visualPhase * 0.32;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * maxRadius * 0.1, Math.sin(angle) * maxRadius * 0.1);
      ctx.lineTo(Math.cos(angle) * maxRadius, Math.sin(angle) * maxRadius);
      ctx.strokeStyle = "rgba(255, 251, 247, 0.14)";
      ctx.lineWidth = Math.max(1, scale / 540);
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(0, 0, maxRadius * 0.12, 0, Math.PI * 2);
    ctx.fillStyle = state.playing ? "rgba(0, 122, 255, 0.95)" : "rgba(0, 122, 255, 0.58)";
    ctx.fill();
    ctx.restore();

    updateTimerDisplay(now);
    window.requestAnimationFrame(drawMandala);
  }

  function bindEvents() {
    el.instructionsButton.addEventListener("click", openInstructions);
    el.closeInstructions.addEventListener("click", closeInstructions);
    el.gotItButton.addEventListener("click", closeInstructions);

    el.instructionsModal.addEventListener("click", function (event) {
      if (event.target === el.instructionsModal) {
        closeInstructions();
      }
    });

    window.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && !el.instructionsModal.hidden) {
        closeInstructions();
      }
    });

    qsa('input[name="useMode"]').forEach(function (input) {
      input.addEventListener("change", function () {
        if (input.checked) {
          setUseMode(input.value);
        }
      });
    });

    qsa('input[name="deviceMode"]').forEach(function (input) {
      input.addEventListener("change", function () {
        if (input.checked) {
          setDeviceMode(input.value);
        }
      });
    });

    el.customToggle.addEventListener("click", function () {
      setCustomOpen(!state.customOpen);
    });

    el.transportButton.addEventListener("click", function () {
      if (state.playing) {
        stopTone();
      } else {
        startTone();
      }
    });

    el.frequencyInput.addEventListener("change", function () {
      setFrequency(el.frequencyInput.value);
    });

    el.frequencySlider.addEventListener("input", function () {
      setFrequency(sliderToFrequency(el.frequencySlider.value));
    });

    qsa("[data-step]").forEach(function (button) {
      button.addEventListener("click", function () {
        setFrequency(state.frequency + Number(button.dataset.step));
      });
    });

    qsa('input[name="timer"]').forEach(function (input) {
      input.addEventListener("change", function () {
        if (input.checked) {
          setTimerMinutes(input.value);
        }
      });
    });

    el.volumeSlider.addEventListener("input", function () {
      updateVolume(el.volumeSlider.value);
    });

    el.presetGrid.addEventListener("click", function (event) {
      const button = event.target.closest("button[data-frequency]");
      if (!button) {
        return;
      }

      setFrequency(Number(button.dataset.frequency), {
        preset: true,
        intention: button.dataset.intention
      });
    });

    el.frequencyGuideList.addEventListener("click", function (event) {
      const button = event.target.closest("button[data-frequency]");
      if (!button) {
        return;
      }

      setFrequency(Number(button.dataset.frequency), {
        preset: true,
        intention: button.dataset.intention
      });
      el.frequencyGuideList.closest("details").open = false;
    });

    el.resetButton.addEventListener("click", function () {
      setUseMode("tune");
      setDeviceMode("phone");
      setFrequency(528, { preset: true, intention: copy.defaults.intention });
      updateVolume(14);
      setTimerMinutes(15);
      updateBinauralEnabled(false);
      updateResonanceEnabled(false);
      setCustomOpen(false);
    });

    el.binauralToggle.addEventListener("change", function () {
      updateBinauralEnabled(el.binauralToggle.checked);
    });

    el.brainwaveGrid.addEventListener("click", function (event) {
      const button = event.target.closest("button[data-mode]");
      if (!button) {
        return;
      }
      setBrainwaveMode(button.dataset.mode);
    });

    el.beatInput.addEventListener("change", function () {
      updateBeat(el.beatInput.value);
    });

    el.resonanceToggle.addEventListener("change", function () {
      updateResonanceEnabled(el.resonanceToggle.checked);
    });

    window.addEventListener("pagehide", stopTone);
    document.addEventListener("visibilitychange", syncScreenWakeLock);
    window.addEventListener("resize", sizeCanvas);
  }

  function populateControls() {
    el.frequencyInput.value = Number(state.frequency.toFixed(2));
    el.frequencySlider.value = frequencyToSlider(state.frequency);
    el.volumeSlider.value = state.volume;
    el.volumeReadout.textContent = `${Math.round(state.volume)}%`;
    el.beatInput.value = Number(state.beatHz.toFixed(1));

    const timerInput = qs(`input[name="timer"][value="${state.timerMinutes}"]`);
    if (timerInput) {
      timerInput.checked = true;
    }

    updateBinauralEnabled(state.binauralEnabled);
    updateResonanceEnabled(state.resonanceEnabled);
    updateModePresentation();
    setCustomOpen(state.customOpen, { store: false });
    updateFrequencyControls();
    updateBrainwaveSelection();
    updateTimerDisplay(performance.now());
    updateTransport();
  }

  readStoredState();
  translateStaticText();
  createPresets();
  createFrequencyGuide();
  createBrainwaveModes();
  populateControls();
  bindEvents();
  window.requestAnimationFrame(drawMandala);
})();
